<template>
	<ListPageSearch
		ref="listPageSearch"
		mobile-teleport-target="#list-page-search-container"
		:has-map="true"
		:has-sort="true"
		list-type="hotel"
		:destination-label="hotelListData.destinationName"
	/>
	<div>
		<h1
			v-if="name"
			class="hotel-list__headline"
		>
			{{ name }} Hotels
		</h1>
		<article
			v-if="storeInitialized"
			class="hotel-list"
		>
			<aside class="hotel-list__aside">
				<MapPreviewAndModal
					id="hotel-list-map"
					class="hotel-list__section-google-maps"
					:northwest="geoBoundaries.northwest"
					:southeast="geoBoundaries.southeast"
					:marker="true"
					:show-note="false"
					:hotel-to-show="null"
					:maps-key="GOOGLE_MAPS_API_KEY"
					:hotel-request-body="lastFetchRequestBody"
				>
					<template #default="{ loadFullscreen }">
						<HotelMap
							:ga-attributes="
								gaSelectContentAttr({
									cType: 'ux_hotellist_click',
									cId: 'hotellistmap',
								})
							"
							location=""
							@click-hotel-map="loadFullscreen"
						/>
					</template>
				</MapPreviewAndModal>
				<ListFilters
					class="hotel-list-filters"
					:list-type="'hotel'"
					:destination-label="name"
				/>
			</aside>
			<section class="hotel-list__content">
				<div class="hotel-list__list">
					<FilterTagList
						:filter-number="filtersCount"
						:has-results="hasResults"
						class="hotel-list__filter-tag-list"
					/>
					<div class="hotel-list__top-container">
						<div
							v-if="hasResults && !loading"
							class="hotel-list__count-hotels--dropdown"
						>
							<span class="hotel-list__count-number">{{ selectedDestinationCount }}</span>
							<span
								v-if="selectedDestinationCount"
								class="hotel-list__count-text"
							>
								&nbsp;gefunden</span
							>
						</div>
						<div
							:class="{
								'hotel-list__list-sort-filters': true,
								'hotel-list__list-sort-filters--no-results': !hasResults,
							}"
						>
							<DropdownHotelFilter
								v-if="isDesktop"
								:disabled="!hasResults"
							/>
						</div>
					</div>
					<NoOffersFound
						v-if="!hasResults && !loading"
						class="hotel-list__no-results"
						@OpenFilters="openFilters"
						@OpenSearch="openSearch"
					/>
					<div
						v-if="loading"
						class="hotel-list__load-box"
					>
						<Loading class="hotel-list__loader" />
					</div>
					<HotelListItemList
						v-else
						:items="items"
						:loading="loading"
						:show-suns="showSuns"
						:hotel-list-type="hotelListData.locationType"
						:discounts
					/>
					<div
						class="hotel-list__pager"
						:class="{ 'hotel-list__pager--no-next': !showMoreButton }"
					>
						<RoundedButton
							v-if="showMoreButton"
							class="hotel-list__more-button"
							theme="outlined"
							@click.prevent="() => getMoreItems()"
						>
							Mehr Angebote laden
						</RoundedButton>

						<Loading
							v-if="loadMore"
							class="hotel-list__loader"
						/>

						<a
							v-if="hasResults && !loading"
							href="#"
							class="hotel-list__scroll-top"
							aria-label="Scroll nach oben"
							@click.prevent="scrollTop"
						>
							<span class="hotel-list__scroll-top-label">nach oben</span>
							<svg
								class="hotel-list__scroll-top-icon"
								viewBox="0 0 15 32"
							>
								<path
									d="M0.438 27.125l11.125-11.125-11.125-11.125c-0.625-0.625-0.625-1.563 0-2.188 0.563-0.625 1.563-0.625 2.188 0l12.188 12.25c0.625 0.625 0.625 1.5 0 2.125l-12.188 12.25c-0.625 0.625-1.625 0.625-2.188 0-0.625-0.625-0.625-1.563 0-2.188z"
								/>
							</svg>
						</a>
					</div>
					<QuickPillHotelList
						v-show="!hasDealsFilterActive"
						ref="quickPillHotelList"
						:hotel-list-data="hotelListData"
						class="hotel-list__quick-pill-hotel-list"
					/>
					<div
						id="adSlot"
						class="adSlot-container"
					></div>
				</div>
			</section>
		</article>
	</div>
</template>

<script lang="ts" setup>
import NoOffersFound from '@lmt-rpb/NoOffersFound/NoOffersFound.vue';
import HotelListItemList from '@lmt-rpb/HotelListItemList/HotelListItemList.vue';
import type { AxiosError } from 'axios';
import axios from 'axios';
import DropdownHotelFilter from '@lmt-rpb/DropdownHotelFilter/DropdownHotelFilter.vue';
import type { SearchFormDataType } from '@interfaces/search-form';
import { getAll as getAllParams, updateURL } from '@components/common/services/url';
import { formToParams } from '@components/common/services/transform';
import Loading from '@lmt-rpb/Loading/Loading.vue';
import { pluralize, scrollIt } from '@utils/utils';
import { isClientGermany } from '@utils/environmentUtils';
import { API_URLS, GOOGLE_MAPS_API_KEY, HOTEL_LABELS } from '@global-js/constants';
import RoundedButton from '@lmt-rpb/RoundedButton/RoundedButton.vue';
import ListPageSearch from '@lmt-rpb/ListPageSearch/ListPageSearch.vue';
import ListFilters from '@lmt-rpb/ListFilters/ListFilters.vue';
import FilterTagList from '@lmt-rpb/FilterTagList/FilterTagList.vue';
import { updateBreadcrumbUrl } from '@modules/updateUrls';
import { EventBus } from '@global-js/event-bus';
import { computed, nextTick, onBeforeMount, onBeforeUnmount, onMounted, type Ref, ref, watch } from 'vue';
import type { ItemType, OffersWithLinks, _links } from '@interfaces/hotel-list-types/hotel-list-types';
import * as searchHistoryService from '@components/common/services/localStorage/searchHistoryService';
import { createDiscountRequest, getAsMap as fetchDiscountData } from '@components/common/services/neustaService';
import { fetchTourOperatorInventory } from '@/components/common/services/tourOperatorInventoryService';
import type { HotelListData } from '@/interfaces/components/hotelListData';
import { useStore } from '@/components/common/store';
import { mergeMaps } from '@/js/utils/objectUtils';
import type { DiscountHotelPriceOffer } from '@/interfaces/neusta-discount-price';
import QuickPillHotelList from '@components/QuickPillHotelList/QuickPillHotelList.vue';
import type { ApiDataTypesKeys } from '../common/types';
import { getDiscountHotelPriceOfferMap } from './helpers';
import { getHotelListOffer, NO_RESPONSE_OR_EMPTY_RESPONSE } from '@services/bestOfferForHotelListService';
import type { BestPackageOfferForHotelListResponse } from '@/interfaces/api/v1-best-package-offer-for-hotel-list';
import { storeToRefs } from 'pinia';
import { useBreakpointStore } from 'src/store/breakpointsStore';
import MapPreviewAndModal from '@lmt-rpb/MapPreviewAndModal/MapPreviewAndModal.vue';
import HotelMap from '@lmt-rpb/Molecules/HotelMap/HotelMap.vue';
import { gaSelectContentAttr } from 'src/utils/analytics';
import { fetchGeoInventory } from '@services/geoInventoryService';
import type { BaseOfferForHotelListResponse } from '@interfaces/common';
import { useFilters } from 'src/composables/useFIlters';

const store = useStore();

const listPageSearch = ref<InstanceType<typeof ListPageSearch>>();
const quickPillHotelList = ref<InstanceType<typeof QuickPillHotelList>>();
const items = ref<ItemType[]>([]);
const discounts: Ref<Map<string, DiscountHotelPriceOffer>> = ref(new Map());
const links = ref<_links>({});
const loadMore = ref(false);
const error = ref<AxiosError | null>(null);
const pager = ref(0);
const hotelListData = ref<HotelListData>(document.getElementById('hotel-list-data')?.dataset as unknown as HotelListData);

const serverSideHotelListResponse: BestPackageOfferForHotelListResponse | null = JSON.parse(
	hotelListData.value.serverSideHotelListResponse
);

const loading = ref(true);
const ratingAttributes = ref('');
const storeInitialized = ref(store.state.config.storeInitialized);
const formData = computed((): SearchFormDataType => store.state.searchMask);
const savedFormData = ref<SearchFormDataType>({ ...formData.value }); // Copy of formData
const hasDealsFilterActive = computed(() => store.getters['searchMask/hasDealsFilter']);

const selectedDestinationName = computed((): string => formData.value?.selectedGeoInventory?.Name ?? '');

const selectedDestinationCount = computed((): string => {
	let count: number | null;

	if (store.state.searchMask.deal || formData.value.sortingHotel === 'Discount') {
		count = null;
	} else if (selectedDestinationName.value === '') {
		count = store.state.searchMask ? store.state.searchMask.availableHotelCount : null;
	} else {
		count = store.state.searchMask?.selectedGeoInventory?.Count ?? null;
	}
	return count ? pluralize(count, HOTEL_LABELS.singular, HOTEL_LABELS.plural) : '';
});

const apiData = store.getters['searchMask/apiData'];

const { isDesktop } = storeToRefs(useBreakpointStore());

const locationIdName = computed((): 'RegionID' | 'CityID' => {
	const locationTypeId = hotelListData.value?.locationType;
	return locationTypeId === 'COUNTRY' || locationTypeId === 'REGIONGROUP' ? 'RegionID' : 'CityID';
});

const name = computed((): string | undefined => hotelListData.value?.destinationName);

const showSuns = isClientGermany();

const showMoreButton = computed((): boolean => !!(links.value && links.value.next && !loadMore.value && !loading.value));

const { filtersCount } = useFilters();

const hasResults = computed((): boolean => {
	return items.value.length > 0;
});

let isFirstFetch = true;

// save list before navigating away
const rememberHotelListItems = (): void => {
	const sessionArray = [Date.now(), window.scrollY, items.value, links.value, discounts.value.entries()];
	try {
		sessionStorage.setItem(window.location.href, JSON.stringify(sessionArray));
	} catch (errorSaveList) {
		console.log(errorSaveList);
	}
};

const geoBoundaries = computed(() => {
	try {
		return JSON.parse(hotelListData?.value?.geoBoundaries);
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
	} catch (_error) {
		return null;
	}
});

const sortingParamWasChanged = (): boolean => formData.value.sortingHotel !== savedFormData.value.sortingHotel;

const formDataWasChanged = (): boolean => {
	// Excludes for offerlist update
	/* eslint-disable @typescript-eslint/no-unused-vars */
	const {
		destination: currentDestination,
		searchTerm: currentSearchTerm,
		modified: currentModified,
		availableHotelCount: currentAvailableHotelCount,
		selectedGeoInventory: currentSelectedGeoInventory,
		destinationTypes: currentDestinationTypes,
		destinationTypeName: currentDestinationTypeName,
		maxTravelDurationError: currentError,
		...newFormData
	} = formData.value;
	const {
		destination: savedDestination,
		searchTerm: savedtSearchTerm,
		modified: savedModified,
		availableHotelCount: savedAvailableHotelCount,
		selectedGeoInventory: savedSelectedGeoInventory,
		destinationTypes: savedDestinationTypes,
		destinationTypeName: savedDestinationTypeName,
		maxTravelDurationError: savedError,
		...newSavedFormData
	} = savedFormData.value;
	/* eslint-enable @typescript-eslint/no-unused-vars */

	// Update savedFormData
	savedFormData.value = { ...formData.value };

	if (newFormData.additionalCheckboxFilters?.ratingAttributes) {
		const newRatingAttributes = JSON.stringify(
			newFormData.mostPopularFilters?.ratingAttributes?.concat(newFormData.additionalCheckboxFilters?.ratingAttributes)
		);
		// Check if wanted fields updated
		if (newRatingAttributes !== ratingAttributes.value) {
			ratingAttributes.value = newRatingAttributes;
			return true;
		}
	}

	return JSON.stringify(newFormData) !== JSON.stringify(newSavedFormData);
};

const handlePopState = ({ state }: PopStateEvent): void => {
	EventBus.$emit('window:popstate', state);
};

const handleKeydown = ({ which }: KeyboardEvent): void => {
	const meta = (
		{
			27: 'esc',
		} as { [key: number]: string }
	)[which];

	if (meta) {
		EventBus.$emit(`keydown.${meta}`);
	}
};

const fetchTourOperators = (): void => {
	const listType = hotelListData.value?.locationType;
	const exclude: ApiDataTypesKeys[] = [];

	exclude.push('HotelIffCode');

	if (listType === 'COUNTRY') {
		exclude.push('Regions', 'Cities');
	}
	if (listType === 'REGIONGROUP') {
		exclude.push('Cities');
	}

	const bodyParams = {
		regionGroupIds: listType === 'REGIONGROUP' && hotelListData.value.rlId ? [parseInt(hotelListData.value.rlId, 10)] : [],
		locationId: listType === 'COUNTRY' ? [hotelListData.value.locationId] : [],
		rid: listType === 'TOPREGION' ? [parseInt(hotelListData.value.rid, 10)] : [],
		cyid: listType === 'CITY' ? [parseInt(hotelListData.value.cyid, 10)] : [],
	};

	fetchTourOperatorInventory(bodyParams, exclude);
};

const scrollTop = (): void => scrollIt(0, 1000, 'easeOutQuad');

function openSearch() {
	if (!isDesktop.value) {
		listPageSearch.value!.searchFormModal!.openModal();
	}
}

function openFilters() {
	if (!isDesktop.value) {
		listPageSearch.value!.filterModal!.openModal();
	}
}

function createFetchRequest() {
	const listType: string | undefined = hotelListData.value.locationType;
	const travelType = formData.value.onlyHotel ? 'Accommodation' : 'Package';
	const exclude: ApiDataTypesKeys[] = travelType === 'Accommodation' ? ['DepartureAirport', 'DirectFlight', 'Transfer'] : [];
	exclude.push('DefiniteStartDate', 'HotelIffCode');

	// destinationTypes can be either Regions or Cities
	// so we remove the Cities param here or else Cities & Regions will use the same id
	if (listType === 'COUNTRY') {
		exclude.push('Cities');
	}
	if (listType === 'CITY') {
		exclude.push('Regions');
	}
	if (listType === 'REGIONGROUP') {
		exclude.push('Cities');
	}

	const bodyParams: Partial<SearchFormDataType> = {
		regionGroupIds: listType === 'REGIONGROUP' && hotelListData.value.rlId ? [parseInt(hotelListData.value.rlId, 10)] : [],
		locationId: listType === 'COUNTRY' ? [hotelListData.value.locationId] : [],
		rid: listType === 'TOPREGION' && hotelListData.value.rid ? [parseInt(hotelListData.value.rid, 10)] : [],
		cyid: listType === 'CITY' && hotelListData.value.cyid ? [parseInt(hotelListData.value.cyid, 10)] : [],
		numberOfResults: 10,
	};
	return apiData(bodyParams, exclude);
}

const lastFetchRequestBody = computed(() => {
	const body = createFetchRequest();
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { Cities, Regions, ...mapRequestBody } = body;

	return mapRequestBody;
});

async function fetchItems(url?: string): Promise<BaseOfferForHotelListResponse<ItemType>> {
	const body = createFetchRequest();
	const apiUrl = url || API_URLS.HOTEL_LIST;

	if (JSON.stringify(body.Cities) === JSON.stringify(body.Regions)) {
		body.Cities = '';
	}

	try {
		const response = await getHotelListOffer(String(apiUrl), body, 'hotelListOffers');
		// remove error if any
		error.value = null;
		try {
			sessionStorage.lastfetch = Date.now();
		} catch (errorStorageDate) {
			console.log(errorStorageDate);
		}
		return response;
	} catch (axiosError) {
		const { response } = axiosError as AxiosError;
		if (response && response.status >= 500) {
			error.value = axiosError as AxiosError;
		} else {
			items.value = [];
		}

		throw axiosError;
	}
}

async function getDiscounts(offerItems: ItemType[]) {
	let newItemDiscounts: Map<string, DiscountHotelPriceOffer>;
	const discountRequest = createDiscountRequest(
		offerItems,
		store.state.searchMask.travelers.adult,
		store.state.searchMask.travelers.children
	);
	if (hasDealsFilterActive.value || formData.value.sortingHotel === 'Discount') {
		newItemDiscounts = getDiscountHotelPriceOfferMap(discountRequest as ItemType[]);
	} else {
		newItemDiscounts = await fetchDiscountData(discountRequest);
	}
	if (discounts.value.size === 0) {
		discounts.value = newItemDiscounts;
	} else {
		discounts.value = mergeMaps(discounts.value, newItemDiscounts);
	}
}

const getItems = async (): Promise<void> => {
	loading.value = true;

	// Set server side response if available
	const params = window.location.search;
	if (isFirstFetch && serverSideHotelListResponse && !params.length) {
		items.value = serverSideHotelListResponse.Offers;
		links.value = serverSideHotelListResponse._links ? serverSideHotelListResponse._links : {};
		loading.value = false;
		try {
			await getDiscounts(items.value);
		} catch (requestError) {
			console.error('HotelList: ', requestError);
		}
	} else {
		try {
			const resp = await fetchItems();
			if (!resp || !resp.Offers) {
				pager.value = 0;
			}
			items.value = resp?.Offers || [];

			links.value = resp?._links || {};
			loading.value = false;
			await getDiscounts((resp as OffersWithLinks).Offers);
		} catch (axiosError) {
			if (axios.isCancel(axiosError)) {
				loading.value = true;
			} else if ((axiosError as Error).message === NO_RESPONSE_OR_EMPTY_RESPONSE) {
				loading.value = false;
			} else {
				loading.value = false;
				console.error('HotelList: ', axiosError);
			}
		}
	}
	isFirstFetch = false;
	await fetchGeoInventory(hotelListData, formData.value.onlyHotel, locationIdName);
};

const handleMobileSubmit = (): void => {
	if (!isDesktop.value && formDataWasChanged()) {
		searchHistoryService.update();
		updateURL(formToParams(formData.value, 'hotelList'));
		getItems();
		fetchTourOperators();
		quickPillHotelList.value?.loadMostPopularPlaces();
	}
};

const getMoreItems = async (): Promise<void> => {
	// set loading true
	loadMore.value = true;
	try {
		const resp = await fetchItems(links.value.next);
		// validate response
		if (!resp || !resp.Offers || Array.isArray(resp)) {
			items.value = [];
			links.value = {
				prev: '',
				next: '',
			};
			pager.value = 0;

			return;
		}
		const { _links: responseLinks } = resp;

		items.value = [...items.value, ...resp.Offers];
		links.value = responseLinks || {};
		getDiscounts(resp.Offers);
	} catch (requestError) {
		const { response } = requestError as AxiosError;

		if (response && response.status >= 500) {
			error.value = requestError as AxiosError;
		}
		console.error('HotelList: ', requestError);
	} finally {
		loadMore.value = false;
	}
};

watch(formData.value, () => {
	if (!isDesktop.value && !sortingParamWasChanged()) {
		return;
	}

	if (formDataWasChanged()) {
		searchHistoryService.update();
		updateURL(formToParams(formData.value, 'hotelList'));
		getItems();
		quickPillHotelList.value?.loadMostPopularPlaces();
	}
});

// life cycle events
onBeforeMount((): void => {
	// set window event listeners
	window.addEventListener('popstate', handlePopState);

	// set keydown listener
	document.addEventListener('keydown', handleKeydown);

	// update query params if page is loaded with params
	const params = getAllParams();
	if (Object.keys(params).length) {
		updateURL(formToParams(formData.value, 'hotelList'));
	}

	window.addEventListener('pagehide', rememberHotelListItems.bind(this));
});

onMounted((): void => {
	addThemeMiniMarginClass();
	if (formData.value.additionalCheckboxFilters?.ratingAttributes) {
		ratingAttributes.value = JSON.stringify(
			formData.value.mostPopularFilters?.ratingAttributes?.concat(formData.value.additionalCheckboxFilters?.ratingAttributes)
		);
	}

	const pageReloaded = true;

	// fetch items

	try {
		const lastSessionArray = JSON.parse(sessionStorage.getItem(window.location.href) as string);
		if (lastSessionArray && Date.now() < lastSessionArray[0] + 3600000 && !pageReloaded) {
			// 3600000 is 1 hour in ms
			fetchGeoInventory(hotelListData, formData.value.onlyHotel, locationIdName);
			items.value = lastSessionArray[2];
			loading.value = false;
			links.value = lastSessionArray[3] || {};
			discounts.value = new Map(lastSessionArray[4]);
			nextTick(() => window.scrollTo(0, lastSessionArray[1]));
		} else {
			getItems();
		}
	} catch {
		getItems();
	}

	fetchTourOperators();

	EventBus.$on('SearchMask:MobileSubmit', handleMobileSubmit);

	// Update breadcrumb URLs
	EventBus.$on('url:change', updateBreadcrumbUrl);
	// Initial update of URLs
	updateBreadcrumbUrl();
});

onBeforeUnmount((): void => {
	window.removeEventListener('popstate', handlePopState);
	document.removeEventListener('keydown', handleKeydown);
	EventBus.$off('SearchMask:MobileSubmit', handleMobileSubmit);
	EventBus.$off('url:change', updateBreadcrumbUrl);

	window.removeEventListener('pagehide', rememberHotelListItems);
});

function addThemeMiniMarginClass() {
	document.body.classList.add('show-theme-mini-margin');
}
</script>

<style lang="scss" scoped>
$scroll-color: $color-primary-l1;

:deep(.filter-button__content) {
	display: flex;
	flex-direction: row-reverse;
}

:deep(.filter-button--with-filters) {
	border: none;
	background-color: $color-vivid;
}

:deep(.filter-button__label-num) {
	display: flex;
	font-size: $font-medium-2;
	font-weight: $font-weight-bold;
	margin-right: 1rem;

	& > span {
		margin-left: 0.6rem;
	}
}

:deep(.filter-button) {
	width: auto;
	border: none;
	background-color: $color-vivid;
}

:deep(.filter-tag-list__mobile-title) {
	display: none;
}

:deep(.hotel-map__container) {
	height: 9.8rem;
	margin-bottom: 1.6rem;

	svg {
		width: 35rem;
		position: relative;
		top: 19rem;
		transform: scale(1.6);
	}
}

.hotel-list {
	display: flex;
	flex-direction: column;
	margin: 3rem auto 0;
	scrollbar-color: $color-primary transparent;
	scrollbar-width: thin;
	-webkit-tap-highlight-color: transparent;

	::-webkit-scrollbar {
		width: 1.2rem;
	}

	::-webkit-scrollbar-thumb {
		border-radius: $border-radius-small;
		background-color: $color-primary;
	}

	&__filter-tag-list {
		@media (min-width: $breakpoint-extralarge) {
			margin-bottom: 1.6rem;
		}
	}

	&__headline {
		text-align: center;
		padding-top: 3rem;
		padding-bottom: 0;
	}

	&__aside {
		display: none;

		&--visible {
			display: block;
		}
	}

	.hotel-list-filters {
		max-width: 35rem;
	}

	&__list-sort-filters {
		gap: 1rem;
		width: 100%;

		@media (min-width: $breakpoint-verysmall) {
			width: unset;
			margin-bottom: 0.8rem;
		}
	}

	&__filter {
		display: flex;
		justify-content: space-between;
		background-color: $color-vivid;
		margin-bottom: 2.4rem;
		min-height: 5.6rem;
	}

	&__top-container {
		margin: 0 0 0.8rem;
		display: flex;
		justify-content: left;

		@media (min-width: $breakpoint-verysmall) {
			margin: 0;
		}

		@media (min-width: $breakpoint-large) {
			margin: 0 0 2.4rem;
		}

		@media (min-width: $breakpoint-medium) {
			justify-content: space-between;
		}

		@media screen and (min-width: $breakpoint-extralarge) {
			max-width: 105.4rem;
			align-items: end;
		}

		&--no-results {
			margin: 0;
		}

		& > *:only-child {
			margin-left: auto;
		}
	}

	&__count-hotels {
		background: $color-filter-bg;
		border-radius: 0.5rem;
		padding: 0 1.6rem;
		font-size: $font-small-1;
		font-weight: $font-weight-semibold;
		font-family: $font-family-special;
		margin: 1.6rem;
		min-height: 3.1rem;
		display: flex;
		align-items: center;

		&--dropdown {
			font-family: $font-family-special;
			font-size: $font-medium-3;
			line-height: $line-height-text-small;
			white-space: nowrap;

			@media screen and (min-width: $breakpoint-extralarge) {
				font-size: $font-medium-1;
			}
		}

		span {
			font-weight: $font-weight-regular;
		}

		@media screen and (min-width: $breakpoint-extralarge) {
			background: transparent;
		}

		@media screen and (min-width: $breakpoint-medium) {
			line-height: 1.3rem;
			align-self: flex-end;
		}
	}

	&__count-number {
		font-weight: $font-weight-semibold;
	}

	&__switch-sort {
		display: none;
	}

	&__list {
		position: relative;
		z-index: 0;
	}

	&__no-results {
		position: relative;
		margin-top: 5.3rem;

		@media screen and (min-width: $breakpoint-extralarge) {
			margin-left: 0.36rem;
			margin-top: 0;
		}
	}

	.hotelrating .hotelrating__icon path {
		fill: unset;
	}

	&__loader {
		margin: 30rem auto;
	}

	&__more-button {
		display: block;
		margin: 0 auto;
	}

	&__pager {
		padding: 0;
		display: flex;
		max-width: 105.4rem;
		align-items: center;
		position: relative;
		margin: calc(2.4rem - 1em) auto 4rem auto; /* Margin of list above is 1em, total space between pager and list-item should be 2.4rem or 3.2rem (Desktop)*/

		@media (min-width: $breakpoint-extralarge) {
			margin: calc(3.2rem - 1em) auto 4rem auto;
		}

		&--no-next {
			justify-content: flex-end;
		}
	}

	&__scroll-top {
		text-decoration: none;
		display: flex;
		align-items: center;
		gap: 0.8rem;
		position: absolute; /* Parent is relative. This is needed because this element shouldn't take up space */
		right: 0;
	}

	&__scroll-top-label {
		@include visible-from($breakpoint-extralarge, inline-block);
	}

	&__scroll-top-icon {
		width: 1.5rem;
		height: 1.5rem;
		transform: rotate(-90deg);
		fill: currentcolor;
		vertical-align: text-top;
	}

	@include media-query-up($breakpoint-large) {
		flex-direction: row;

		&__content {
			width: 100%;
			margin-left: 0;
		}
	}

	@include media-query-up($breakpoint-extralarge) {
		flex-direction: row;
		max-width: 144rem;

		&__switch-sort {
			display: block;
		}

		&__headline {
			text-align: left;
			padding-left: 0;
			padding-right: 0;
			max-width: 144rem;
			margin-right: auto;
			margin-left: auto;
			border-bottom: 0.1rem solid $color-lime-light;
		}

		&__aside {
			width: 25%;
			display: block;
			min-width: 35rem;
		}

		&__content {
			width: 75%;
			min-width: 0;
			margin-left: 2.5rem;
		}
	}

	&__quick-pill-hotel-list {
		margin-bottom: 4rem;
	}
}
</style>
